import { AptiveIcon } from '@aptive-env/storybook'
import { ICalendarEventType } from 'interface/calendar'
import { FC, useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import { getCalendarEventTypeColors } from 'utils/color'

interface CalendarEventTypeSelectProps {
  items: ICalendarEventType[]
  selected: ICalendarEventType | null
  onSelect: (selected: ICalendarEventType) => void
  disabled?: boolean
}

export const CalendarEventTypeSelect: FC<CalendarEventTypeSelectProps> = ({
  items,
  selected,
  onSelect,
  disabled = false,
}) => {
  const ref = useRef<HTMLDivElement>(null)
  const [isExpanded, setIsExpanded] = useState(false)

  const handleSelect = (item: ICalendarEventType) => {
    setIsExpanded(false)
    onSelect(item)
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        event.target instanceof HTMLElement &&
        ref.current &&
        !ref.current.contains(event.target)
      ) {
        event.stopPropagation()
        setIsExpanded(false)
      }
    }

    document.addEventListener('click', handleClickOutside, true)
    return () => {
      document.removeEventListener('click', handleClickOutside, true)
    }
  }, [])

  return (
    <div className="pl-2">
      <div className="w-full relative">
        <div
          className={classNames(
            'flex items-center justify-between',
            disabled ? 'text-gray-400' : 'text-dark-gray-100 cursor-pointer',
          )}
          onClick={disabled ? () => {} : () => setIsExpanded((prev) => !prev)}
        >
          {selected && (
            <div className="h-9 flex items-center whitespace-nowrap px-3">
              <div
                className="h-4 w-4 rounded-full"
                style={{
                  background: getCalendarEventTypeColors(selected.id).dark,
                }}
              />
              <div className="ml-2 text-[14px]">
                {selected.name}
              </div>
            </div>
          )}
          <AptiveIcon
            className="w-[20px] h-[20px] stroke-aro-gray fill-none"
            icon={isExpanded ? 'chevronUp' : 'chevronDown'}
          />
        </div>
        {isExpanded && (
          <div
            ref={ref}
            className="shadow rounded-lg overflow-hidden absolute z-[100] bg-white w-full"
          >
            {items?.map((item, index) => (
              <div
                key={index}
                className="h-9 flex items-center whitespace-nowrap px-3 cursor-pointer border-b hover:bg-ui-gray-100"
                onClick={() => handleSelect(item)}
              >
                <div
                  className="h-4 w-4 rounded-full"
                  style={{
                    background: getCalendarEventTypeColors(item.id).dark,
                  }}
                />
                <div className="ml-2 text-[14px] text-[#4B5563]">
                  {item.name}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
